import { useCallback, useEffect, useRef } from 'react';

/**
 * Ensures at most one instance of `sync` is in-flight at a time.
 *
 * Returns a trigger function to start `sync`.
 *
 * Behavior:
 * - While `sync` is in-flight, additional trigger() calls are ignored.
 * - Once `sync` completes (success or failure), the next trigger() call will re-invoke it.
 * - If `enabled` is false, sync will not be invoked. However, if it is already running, changes to `enabled` will not affect the running instance.
 * - When `enabled` transitions from `false` to `true`, trigger() fires automatically.
 */
export const useSyncWorker = (sync: () => Promise<void>, enabled?: boolean) => {
  const isRunning = useRef(false);
  const isEnabled = useRef(enabled);
  isEnabled.current = enabled;

  const start = useCallback(() => {
    if (isEnabled.current === false || isRunning.current) return;
    isRunning.current = true;
    sync()
      .catch(error => console.error('Sync worker error:', error))
      .finally(() => isRunning.current = false);
  }, [sync]);

  // Auto-trigger when enabled transitions from false to true
  useEffect(start, [enabled, start]);

  return { start, isRunning: isRunning.current };
};
